import React, { useEffect, useRef } from 'react';
import { Box, Dialog, DialogContent, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Gift, Minimize2, Pause, Play, RotateCcw, Timer } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useLocation } from 'react-router-dom';
import { useFocusTools } from '../contexts/FocusToolsContext';
import { formatCountdown } from '../utils/usePomodoro';
import PomodoroClock from './PomodoroClock';
import { TimerSoundButton, TimerWindowButton } from './PomodoroTimer';
import TimerWindow from './TimerWindow';
import { ToolButton, toolTextSx } from './ToolCard';

function RewardConfetti() {
  const canvas = useRef(null);
  const theme = useTheme();
  useEffect(() => {
    const burst = confetti.create(canvas.current, { resize: true });
    burst({ particleCount: 140, spread: 100, origin: { y: 0.55 },
      colors: [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.green.main],
      disableForReducedMotion: true });
    return () => burst.reset();
  }, [theme]);
  return <Box component="canvas" ref={canvas} aria-hidden="true" sx={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: (theme) => theme.zIndex.modal + 2 }} />;
}

export default function FocusToolsOverlay() {
  const { state, remainingMs, act, focusOpen, setFocusOpen, windowError } = useFocusTools();
  const location = useLocation();
  const { timer } = state;
  const active = ['running', 'paused'].includes(timer.status);
  const rewardOpen = timer.status === 'finished' && !timer.acknowledged;
  const isFocus = timer.phase === 'focus';
  const minimize = () => setFocusOpen(false);

  return (
    <>
      <TimerWindow />
      {focusOpen && active && <Dialog fullScreen open onClose={minimize} aria-labelledby="focus-stage-title" transitionDuration={0}
        PaperProps={{ sx: (theme) => ({ background: `radial-gradient(ellipse at 50% 40%, ${alpha(theme.palette.primary.main, 0.07)}, transparent 65%), ${theme.palette.background.default}` }) }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: { xs: 2, sm: 4 }, py: 2 }}>
          <Typography sx={{ ...toolTextSx, color: 'secondary.main' }}>Pomodoro</Typography>
          <Stack direction="row" gap={1}>
            <TimerSoundButton />
            <TimerWindowButton iconOnly />
            <Tooltip title="Minimize timer"><IconButton onClick={minimize} aria-label="Minimize timer" sx={{ color: 'primary.main', width: 44, height: 44 }}><Minimize2 size={22} /></IconButton></Tooltip>
          </Stack>
        </Stack>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: { xs: 2, sm: 3 }, px: 2, pb: 5 }}>
          <Typography component="h2" id="focus-stage-title" sx={{ fontSize: { xs: '1rem', sm: '1.4rem' }, lineHeight: 1.8, textAlign: 'center', m: 0 }}>
            {timer.status === 'paused' ? 'Paused' : isFocus ? 'Focus time' : 'Break time'}
          </Typography>
          <PomodoroClock timer={timer} remainingMs={remainingMs} expanded />
          <Stack direction="row" alignItems="center" gap={2}>
            <ToolButton variant="contained" startIcon={timer.status === 'paused' ? <Play size={20} /> : <Pause size={20} />}
              onClick={() => act(timer.status === 'paused' ? 'resume' : 'pause')} sx={{ minWidth: 170 }}>
              {timer.status === 'paused' ? 'Resume timer' : 'Pause timer'}
            </ToolButton>
            <Tooltip title="Reset timer"><IconButton onClick={() => act('reset')} aria-label="Reset timer" sx={{ color: 'secondary.main', width: 48, height: 48 }}><RotateCcw size={22} /></IconButton></Tooltip>
          </Stack>
          {!isFocus && timer.reward && <Typography sx={{ ...toolTextSx, maxWidth: 500, overflowWrap: 'anywhere' }}>{timer.reward}</Typography>}
          {windowError && <Typography role="alert" sx={{ ...toolTextSx, maxWidth: 400, color: 'secondary.main' }}>{windowError}</Typography>}
        </DialogContent>
      </Dialog>}
      {rewardOpen && <Dialog open onClose={() => act('acknowledge')} aria-labelledby="focus-reward-title" maxWidth="sm" fullWidth transitionDuration={0}
        PaperProps={{ sx: (theme) => ({
          m: 2, p: { xs: 3, sm: 5 }, width: 'calc(100% - 32px)', borderRadius: '32px', textAlign: 'center',
          border: `3px solid ${theme.palette.green.main}`, overflowWrap: 'anywhere',
          background: `radial-gradient(ellipse at top, ${alpha(theme.palette.secondary.main, 0.09)}, transparent 70%), ${theme.palette.background.default}`,
          boxShadow: `0 0 50px ${alpha(theme.palette.secondary.main, 0.25)}`,
          '@keyframes rewardPop': { from: { opacity: 0, transform: 'scale(0.85) translateY(12px)' }, to: { opacity: 1, transform: 'scale(1) translateY(0)' } },
          animation: 'rewardPop 250ms ease-out', '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }) }}>
        <Box sx={{ color: 'green.main', mb: 2 }} aria-hidden="true">{isFocus ? <Gift size={56} /> : <Timer size={56} />}</Box>
        <Typography component="h2" id="focus-reward-title" sx={{ ...toolTextSx, color: 'green.main', mb: 3, textAlign: 'center' }}>{isFocus ? 'Focus complete!' : 'Break complete!'}</Typography>
        <Typography component="p" sx={{ fontSize: { xs: '1.1rem', sm: '1.6rem' }, lineHeight: 1.9, color: 'secondary.main', m: 0, mb: 4 }}>
          {isFocus ? timer.reward || 'Time for a break.' : 'Ready when you are.'}
        </Typography>
        <Stack gap={2}>
          <ToolButton variant="contained" onClick={() => act('start', { phase: isFocus ? 'break' : 'focus' })} aria-label={isFocus ? 'Start break' : 'Start focus'}>
            {isFocus ? `Start ${state.breakMinutes} min break` : 'Start focus'}
          </ToolButton>
          <ToolButton onClick={() => act('acknowledge')}>Done</ToolButton>
        </Stack>
      </Dialog>}
      {rewardOpen && isFocus && <RewardConfetti />}
      {active && !focusOpen && !/^\/tools\/pomodoro\/?$/.test(location.pathname) && (
        <ToolButton variant="contained" onClick={() => setFocusOpen(true)} aria-label="Open focus timer"
          sx={{ position: 'fixed', right: 16, bottom: { xs: 110, sm: 24 }, zIndex: 1250, gap: 1.5 }}>
          <Timer size={18} /> {timer.status === 'paused' ? 'Paused' : isFocus ? 'Focus' : 'Break'} · {formatCountdown(remainingMs)}
        </ToolButton>
      )}
    </>
  );
}
